import { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Divider,
  Flex,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Switch,
  Text,
} from '@chakra-ui/react';
import {
  DisplayAggregateQuestionDto,
  DisplayConfigQuestionDto,
  DisplayIndividualQuestionDto,
} from '@keyops-cep/api-client';
import { startCase } from 'lodash';

import useComponentFlag from '../../hooks/useComponentFlag';
import { useUpdateQuestionMetadata } from '../../hooks/useUpdateQuestionMetadata';
import ClearAllFiltersBar from '../../pages/EngagementDetails/components/ClearAllFiltersBar';
import FilterBar from '../../pages/EngagementDetails/components/FilterBar';
import { AnswerDataWithDemographic } from '../../pages/EngagementDetails/types';
import { COMPONENTS } from '../../utils/componentDisplayFlags';
import { buildQuestionMetadataForUpsert } from '../../utils/functions/visualization-actions';
import { getPrevAvgAnnotation } from '../../utils/functions/visualization-utils';
import i18n from '../../utils/i18n';
import { KeyOpsLinkButton } from '../ButtonComponents';

import { EditDisplayConfigPanel } from './EditDisplayConfigPanel';
import ExpandedViewOptions from './ExpandedViewOptions';
import { getVisualizationComponent, VisualizationChartRef } from '.';

/**
 * This modal expands the view of a question's answers visualization.
 * It  can be displayed in 2 separate contexts, hence the optional props:
 * - On click on expand a question in surveys results page. The modal is handled via a hash-based URL navigation and supports navigation between questions
 * - On click on expand a question in comparative analysis of 2 questions. The modal is then controlled: question and questionAnswers are directly passed as props. It does not support navigation.
 */
export const SharedExpandedViewModal = ({
  isOpen,
  onClose,
  currentQuestion,
  currentQuestionAnswers,
  displayPrevAvg,
  togglePrevAvgDisplay,
  hideFilterBar,
  goPrevious,
  goNext,
  canGoPrevious,
  canGoNext,
  navigateQuestionsWithKeys,
  isControlled,
  displayDataTable,
  toggleDataTableDisplay,
  displayEditToggle,
  toggleEditDisplay,
}: {
  isOpen: boolean;
  onClose: () => void;
  currentQuestion: DisplayAggregateQuestionDto | DisplayIndividualQuestionDto;
  currentQuestionAnswers: AnswerDataWithDemographic[];
  displayPrevAvg?: boolean;
  togglePrevAvgDisplay?: () => void;
  hideFilterBar?: boolean;
  goPrevious?: () => void;
  goNext?: () => void;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
  navigateQuestionsWithKeys?: (e: KeyboardEvent<HTMLElement>) => void;
  isControlled: boolean; // True in the context of a question comparison, false in survey's results page (modal handled by URL navigation)
  displayDataTable?: boolean;
  toggleDataTableDisplay?: () => void;
  displayEditToggle?: boolean;
  toggleEditDisplay?: () => void;
}) => {
  const modalVisualizationRef = useRef<VisualizationChartRef>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const { mutate: updateQuestionMetadata } = useUpdateQuestionMetadata();

  // Editable states
  const [label, setLabel] = useState<string>(currentQuestion.label);
  const [displayConfig, setDisplayConfig] = useState<DisplayConfigQuestionDto>({
    chartTitle: currentQuestion.displayConfig?.chartTitle ?? '',
    xAxisTitle: currentQuestion.displayConfig?.xAxisTitle ?? '',
    yAxisTitle: currentQuestion.displayConfig?.yAxisTitle ?? '',
    orderBy: currentQuestion.displayConfig
      ?.orderBy as DisplayConfigQuestionDto['orderBy'],
  });

  // Reseed the displayConfig when navigating to a different question
  useEffect(() => {
    setLabel(currentQuestion.label);
    setDisplayConfig({
      chartTitle: currentQuestion.displayConfig?.chartTitle ?? '',
      xAxisTitle: currentQuestion.displayConfig?.xAxisTitle ?? '',
      yAxisTitle: currentQuestion.displayConfig?.yAxisTitle ?? '',
      orderBy: currentQuestion.displayConfig
        ?.orderBy as DisplayConfigQuestionDto['orderBy'],
    });
  }, [currentQuestion]);

  // Build the question with the editable fields
  const editableQuestion = useMemo(
    () =>
      displayEditToggle
        ? { ...currentQuestion, displayConfig: displayConfig, label }
        : currentQuestion,
    [displayEditToggle, currentQuestion, displayConfig, label]
  );

  // Memoize viz build so inputs don’t get remounted
  const { component: ModalVisualizationComponent } = useMemo(
    () =>
      getVisualizationComponent(
        editableQuestion,
        currentQuestionAnswers,
        modalVisualizationRef,
        displayDataTable,
        false,
        true,
        displayPrevAvg
      ),
    [editableQuestion, currentQuestionAnswers, displayDataTable, displayPrevAvg]
  );

  const questionTypeDisplayText = `${startCase(currentQuestion.type)}${
    currentQuestion.subType ? ' - ' + startCase(currentQuestion.subType) : ''
  }`;

  const prevAvgAnnotation = getPrevAvgAnnotation(currentQuestion);
  const showPrevAvgToggle = useComponentFlag(COMPONENTS.PREV_AVG_TOGGLE);

  const onSave = () => {
    if (label === '') setLabel(currentQuestion.originalLabel); // if label was cleared, revert to originalLabel

    updateQuestionMetadata({
      newQuestionMetadata: buildQuestionMetadataForUpsert(
        editableQuestion,
        label
      ),
      closeEditPanel: toggleEditDisplay,
    });
  };
  return (
    <Modal
      id="shared-expanded-view-modal"
      data-testid="shared-expanded-modal"
      isOpen={isOpen}
      onClose={() => {
        if (displayEditToggle && toggleEditDisplay) toggleEditDisplay();
        onClose();
      }}
      isCentered
      size="6xl"
      initialFocusRef={closeButtonRef}
    >
      <ModalOverlay />
      <ModalContent
        height="95vh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
        tabIndex={0} // make it focusable to allow keys navigation
        onKeyDown={navigateQuestionsWithKeys}
      >
        <ModalHeader fontWeight="normal" position="relative">
          {/* Questions navigation and close button */}
          <Flex
            justifyContent="space-between"
            alignItems="center"
            mb={3}
            mt={'-1px'} // to align with the close icon button
          >
            <Flex alignItems="center">
              {!isControlled && goPrevious && canGoPrevious && (
                <KeyOpsLinkButton onClick={goPrevious}>
                  <ChevronLeftIcon color="keyops.blue" />
                  {i18n.t('visualization.questionsNavigation.previous')}
                </KeyOpsLinkButton>
              )}
            </Flex>
            <Flex alignItems="center" gap={2} justifyContent="flex-end">
              {!isControlled && goNext && canGoNext && (
                <KeyOpsLinkButton onClick={goNext} mr={4}>
                  {i18n.t('visualization.questionsNavigation.next')}
                  <ChevronRightIcon color="keyops.blue" />
                </KeyOpsLinkButton>
              )}
              <ModalCloseButton tabIndex={-1} ref={closeButtonRef} />
            </Flex>
          </Flex>

          <Text fontSize="xs">
            {i18n.t('engagementDetails.questionCards.question', {
              questionNo: currentQuestion.position + 1,
            })}
          </Text>

          <Heading as="h1" size="sm" mt={2}>
            {editableQuestion.label}
          </Heading>

          <HStack spacing={6} mt={2}>
            <Text fontSize="xs">
              {i18n.t('engagementDetails.questionCards.questionType', {
                type: questionTypeDisplayText,
              })}
            </Text>
            <Text fontSize="xs">
              {i18n.t('engagementDetails.questionCards.questionAnswered', {
                answeredNo: currentQuestionAnswers?.length,
              })}
            </Text>
          </HStack>

          <Divider mt={2} ml={-6} width="110%" />

          <Flex
            wrap="wrap"
            align="center"
            justify="flex-start"
            mt={2}
            gap={2}
            hidden={!!hideFilterBar}
          >
            <FilterBar visualization={currentQuestion.visualization ?? ''} />
            <ClearAllFiltersBar />
          </Flex>
        </ModalHeader>

        {!!prevAvgAnnotation && showPrevAvgToggle && (
          <Flex justifyContent="flex-end" w="95%">
            <Box>
              <Switch
                id="display-prev-avg"
                size="sm"
                isChecked={displayPrevAvg}
                onChange={togglePrevAvgDisplay}
              />
              <Text display="inline" fontSize="sm">
                {i18n.t('engagementDetails.questionCards.showPrevAvg')}
              </Text>
            </Box>
          </Flex>
        )}

        <ModalBody
          flex="1"
          minH={0}
          p={0}
          display="flex"
          alignItems="stretch"
          justifyContent="space-between"
          overflow="hidden"
        >
          {/* Visualization column */}
          <Box flex="1" h="100%" p={3} minW={0} overflow="auto">
            {ModalVisualizationComponent}
          </Box>
          {/* Edit drawer */}
          <Box
            aria-label="edit-drawer"
            role="region"
            h="100%"
            width={displayEditToggle ? '400px' : '0px'}
            transition="width 0.5s ease"
            borderTopLeftRadius="4px"
            background="keyops.whiteSmoke"
            pointerEvents={displayEditToggle ? 'auto' : 'none'}
          >
            {displayEditToggle && (
              <EditDisplayConfigPanel
                label={label}
                setLabel={setLabel}
                displayConfig={displayConfig}
                setDisplayConfig={setDisplayConfig}
                onClose={toggleEditDisplay}
                onSave={onSave}
              />
            )}
          </Box>
          {/* Options (hidden while editing) */}
          {displayEditToggle !== true && (
            <ExpandedViewOptions
              visualizationRef={modalVisualizationRef}
              question={currentQuestion}
              displayDataTable={displayDataTable}
              toggleDataTableDisplay={toggleDataTableDisplay}
              displayEditToggle={displayEditToggle}
              toggleEditDisplay={toggleEditDisplay}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
